import { useMemo } from 'react';
import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useMy, UseMyOptions } from '@/hooks/useMy';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { useAuth } from '@/state/auth';
import { Account, LinkAccount } from '@/types';
import {
  type AccountCache,
  removeAccountFromCache,
  upsertAccountInCache,
} from '@/utils/accountCache.util';
import { useMutation } from '@/utils/api';
import { getChariotWebhookURL } from '@/utils/integration.util';
import { capitalize } from '@/utils/lodash.util';

/**
 * Modify account integration with automatic success toast
 *
 * Default behavior: Shows appropriate toast based on action (connected/disconnected/updated)
 * Override: Pass ignoreToast: true to suppress all toasts (legacy parameter)
 * OR: Pass { silent: true } in variables to suppress all toasts (success + error)
 *
 * Note: Migrating to silent pattern, but keeping ignoreToast for backwards compatibility.
 *
 * @param action - Type of modification: 'link', 'unlink', 'update', or 'updateSetting'
 * @param email - Optional email for impersonation
 * @param ignoreToast - Legacy: suppress all toasts (success + error)
 *
 * @example
 * // Standard usage (automatic toast)
 * const { mutate: link } = useModifyAccount('link');
 * link({ username: 'github', config: { token: 'xxx' } });
 *
 * @example
 * // Silent update (no success toast, no error toast - component handles toasts)
 * link({ username: 'github', config: { token: 'xxx' }, silent: true });
 *
 * @example
 * // Legacy: suppress all toasts
 * const { mutate: link } = useModifyAccount('link', undefined, true);
 */
export const useModifyAccount = (
  action: 'link' | 'unlink' | 'update' | 'updateSetting' | 'refreshToken',
  email?: string,
  ignoreToast?: boolean
) => {
  const { me, api, friend } = useAuth();
  const doNotImpersonate = email === me;
  const axios = useAxios({ doNotImpersonate, email });

  // ENG-4601: single source of truth for the account-list cache key, reused for
  // BOTH the optimistic seed and the reconcile invalidation below. This is the
  // exact key the mounted account-list reader (Settings → Notifications,
  // integrations, user management, …) subscribes to — `getMy('account')` plus
  // the trailing impersonation user-scope segment appended by `useGetUserKey`.
  // Non-impersonating: ['MY','account']; impersonating: ['MY','account', friend]
  // (byte-identical to the key the ENG-4586 seed targeted with the same args).
  const accountListKey = useGetUserKey(
    getQueryKey.getMy('account'),
    doNotImpersonate,
    email
  );

  // ENG-4601: seed ONLY the exact, ACTIVE account-list query. This replaces the
  // old fan-out seed (useMy's `updateAllSubQueries`), whose own-principal matcher
  // — under impersonation — ALSO wrote the new row into `useUserSettings`'
  // server-filtered ['MY','account','#…#settings', friend] query, a wrong
  // transient prepend into a filtered subset (Direction 1). `exact:true` is a
  // TanStack deep-equal on the key, so only the unfiltered list is touched,
  // never the filtered/parameterized variants, in BOTH impersonation and "me"
  // (this matches the "me" behavior that was already correct). `type:'active'`
  // preserves the ENG-4586 "safe no-op when no list is mounted" property, and an
  // exact match to the current principal's suffixed key can never bleed into
  // another friend's list.
  const seedAccountList = (
    updater: (prev: AccountCache | undefined) => AccountCache | undefined
  ) => {
    queryClient
      .getQueryCache()
      .findAll({ queryKey: accountListKey, exact: true, type: 'active' })
      .forEach(cache =>
        queryClient.setQueryData<AccountCache>(cache.queryKey, updater)
      );
  };

  return useMutation<Account, Error, LinkAccount & { silent?: boolean }>({
    defaultErrorMessage: 'Failed to modify account',
    mutationFn: async account => {
      try {
        const {
          username,
          config: configAccount,
          member,
          value,
          key,
          role,
          settings,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          silent: _silent,
          ...rest
        } = account;
        const config =
          configAccount || (Object.keys(rest).length ? rest : null);
        if (config) {
          Object.entries(config).forEach(([key, value]) => {
            if (typeof value === 'boolean') {
              config[key] = String(value);
            }
          });
        }
        let data: Account;

        if (action === 'link' || action === 'updateSetting') {
          const response = await axios.post(`/account/${username}`, {
            secret: config,
            value,
            role,
            settings,
            type: account.type,
          });
          data = response.data as Account;
        } else if (action === 'update') {
          const response = await axios.put(`/account/${username}`, {
            value,
            key,
            settings,
          });
          data = response.data as Account;
        } else if (action === 'refreshToken') {
          const response = await axios.patch(`/account/${username}`, {
            key,
            secret: config,
          });
          data = response.data as Account;
        } else {
          const response = await axios.delete(`/account/${username}`, {
            data: {
              secret: configAccount,
              member,
              value,
              key,
              settings,
            },
          });
          data = response.data as Account;
        }

        return data;
      } catch (err: unknown) {
        // Show error toast unless suppressed by ignoreToast OR silent flag
        if (!ignoreToast && !account.silent) {
          const actionText =
            action === 'link'
              ? 'connecting'
              : action === 'updateSetting' || action === 'update'
                ? 'updating'
                : action === 'refreshToken'
                  ? 'refreshing credentials for'
                  : 'disconnecting';

          // Attempt to extract error message for better UX handling
          const error = err as {
            message?: string;
            response?: { data?: { message?: string; error?: string } | string };
          };
          let errorMessage =
            'Please check your username and secret and try again.';
          if (typeof error.response?.data === 'string' && error.response.data) {
            errorMessage = error.response.data;
          } else if (
            typeof error.response?.data === 'object' &&
            error.response.data?.message
          ) {
            errorMessage = error.response.data.message;
          } else if (
            typeof error.response?.data === 'object' &&
            error.response.data?.error
          ) {
            errorMessage = error.response.data.error;
          } else if (error.message) {
            errorMessage = error.message;
          }

          // Strip the "failed to trust SSO:" wrapper to surface the actual error
          const ssoPrefix = 'failed to trust sso:';
          if (errorMessage.toLowerCase().includes(ssoPrefix)) {
            const reason = errorMessage
              .substring(
                errorMessage.toLowerCase().indexOf(ssoPrefix) + ssoPrefix.length
              )
              .trim();
            errorMessage = `SSO setup failed: ${reason}`;
          }

          toast.error(`Error ${actionText} account`, {
            description: errorMessage,
          });
        }
        throw err;
      }
    },
    onSuccess: async (data, account) => {
      const { username } = account;

      const snackbarTitle =
        username in AllIntegrations ? 'integration' : 'account';

      const snackbarAction =
        action === 'link'
          ? 'connected'
          : action === 'updateSetting' ||
              action === 'update' ||
              action === 'refreshToken'
            ? 'updated'
            : 'disconnected';

      if (action === 'link' && username === 'hook') {
        const webhookUrl = getChariotWebhookURL({
          api,
          me: email || friend || me,
          pin: data.value,
        });
        navigator.clipboard.writeText(webhookUrl);
      }

      // Seed the account cache with the server-returned entity (ENG-4586). The
      // list consumers (Settings → Notifications, integrations, user
      // management, …) render from the ['MY','account', …] cache. Previously
      // onSuccess only INVALIDATED with an immediate refetch — but that single
      // refetch races DynamoDB read-after-write, so it frequently re-read stale
      // data (missing the just-created row); this account-resource query sets no
      // per-query staleTime, so it inherits the global ~5-minute staleTime and
      // nothing refetched again, so the change stayed invisible until a manual
      // refresh. Seeding makes the change authoritative and immediate.
      // `seedAccountList` writes ONLY the exact, ACTIVE list query
      // (`accountListKey`), so it's a safe no-op when no list is mounted, never
      // bleeds across impersonated tenants, and (unlike the ENG-4586 fan-out
      // seed) never leaks into the filtered `#…#settings` query (ENG-4601,
      // Direction 1). The helpers no-op when the entity lacks a key or the cache
      // isn't populated yet.
      //
      // ENG-4944: in the empty-cache window (the list mounted but its first
      // mount GET hasn't resolved) that in-flight GET resolves read-after-write
      // STALE and would overwrite whatever we seed here. Rather than CANCEL it —
      // which discards the PRE-EXISTING rows that GET carries (the regression two
      // reviewers caught, on `link` at F1 and on `update`/`unlink` at F2) — we
      // apply the optimistic mutation immediately for instant feedback, then, if
      // the exact GET is still in flight, let it LAND and RE-APPLY the same
      // mutation on top (wait-and-reapply). By query-core's ordering the cache is
      // updated before `query.promise` resolves, so the re-apply always overlays
      // the just-landed list. Net result: create -> [new, ...pre-existing];
      // update -> edited row overlaid on the full list; unlink -> full list minus
      // the row. All list writes stay scoped `exact: true` to the current
      // principal's suffixed `accountListKey` — never a broad predicate — so
      // tenant isolation is preserved (ENG-4400).

      // ENG-4944: one optimistic updater, applied immediately AND re-applied after the
      // in-flight mount GET lands (wait-and-reapply). Idempotent: upsert overlays/prepends,
      // remove drops. `seedIfEmpty` seeds a first page ONLY on create (link) so the new row
      // shows instantly in the empty-cache window before the GET resolves.
      const removedKey = account.key ?? data?.key;
      const applyOptimistic = (
        prev: AccountCache | undefined
      ): AccountCache | undefined => {
        if (action === 'unlink') {
          return removeAccountFromCache(prev, removedKey) ?? prev;
        }
        if (data?.key) {
          return (
            upsertAccountInCache(prev, data, {
              seedIfEmpty: action === 'link',
            }) ?? prev
          );
        }
        return prev;
      };

      // Apply the optimistic mutation immediately (instant feedback; in the empty-cache
      // window the create path seed-creates the first page).
      seedAccountList(applyOptimistic);

      // ENG-4601/ENG-4944: fire the success toast and the disjoint asset/credential
      // invalidations immediately — they don't depend on the account-list reapply below
      // and must not lag behind an unrelated in-flight list GET (the await block only
      // concerns accountListKey).

      // Assets are re-derived server-side from account changes and are NOT
      // seeded, so keep refetching them immediately (unchanged behavior).
      queryClient.invalidateQueries({
        queryKey: getQueryKey.getMy('asset'),
      });

      // Also invalidate credential queries so the AWS "Legacy" badge
      // (useLegacyAwsAccounts) reflects credentials created by this link
      // instead of showing a stale legacy chip until manual refresh. Uses the
      // same broad predicate style as the account invalidation below so it
      // covers impersonated/all-tenants/friend-suffixed keys.
      queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey as string[];
          return key[0] === 'MY' && key[1] === 'credential';
        },
      });

      // Show success toast unless suppressed by ignoreToast (legacy) or silent flag
      if (!ignoreToast && !account.silent) {
        // Show success snackbar
        toast.success(`${capitalize(snackbarTitle)} ${snackbarAction}`, {
          description:
            username === 'hook'
              ? action === 'link'
                ? 'Webhook URL copied to clipboard.'
                : 'Webhook URL was destroyed.'
              : `Your ${snackbarTitle} has been successfully ${snackbarAction}.`,
        });
      }

      // ENG-4944 (F1/F2): if the exact account-list GET is still in flight, it will
      // resolve read-after-write STALE and overwrite our optimistic write. Rather than
      // CANCEL it (which discards the pre-existing rows it carries — the regression two
      // reviewers caught), let it LAND and re-apply the optimistic mutation on top. By
      // query-core's ordering the cache is updated before `query.promise` resolves, so the
      // re-apply always overlays the just-landed list: create -> [new, ...pre-existing];
      // update -> edited row overlaid on the full list; unlink -> full list minus the row.
      // Scoped exact:true to the current principal's suffixed accountListKey — never a
      // broad predicate — preserving tenant isolation (ENG-4400).
      //
      // DECOUPLE the reconcile from mutation completion (ENG-4944 / ENG-4601, PR#6931
      // review P1): the earlier revision `await`ed `listQuery.promise` before reapplying.
      // query-core awaits `onSuccess` before dispatching the mutation's `success` action,
      // so awaiting an unrelated in-flight list GET kept the MUTATION `pending` — stalling
      // every caller that gates on `mutateAsync`/`isPending` (the Refresh Credentials modal
      // in Integrations.tsx sat spinning until the GET happened to resolve). The list
      // reconcile does not need to block the mutation resolving, so we fire it and forget:
      // `onSuccess` returns synchronously and the mutation resolves promptly, while the
      // reapply lands later on the floating promise.
      const listQuery = queryClient
        .getQueryCache()
        .find<AccountCache>({ queryKey: accountListKey, exact: true });
      const inFlightListGet =
        listQuery?.state.fetchStatus === 'fetching' && listQuery.promise
          ? { queryKey: listQuery.queryKey, promise: listQuery.promise }
          : undefined;

      // Mark the exact, seeded list stale WITHOUT refetching (ENG-4601). refetchType
      // 'none' is deliberate — forcing an immediate refetch here would re-run the very
      // DynamoDB read-after-write race and clobber the seed we just wrote. The next
      // natural trigger (tab remount / window focus / staleTime access) reconciles it
      // against the by-then consistent backend.
      //
      // ORDERING GUARANTEE: this MUST run AFTER the reapply `setQueryData`, never before.
      // In query-core 5.90.8 a completing fetch and `setData` both dispatch a `success`
      // action whose reducer sets `isInvalidated: false` (see query-core `successState`).
      // If we invalidated synchronously here (before the GET lands + reapply), the GET's
      // completion and the reapply would immediately CLEAR isInvalidated and silently lose
      // the stale-marking — so in the in-flight branch this is deferred into `.then` AFTER
      // the reapply (and into `.catch`), never called before it.
      const markAccountListStale = () =>
        queryClient.invalidateQueries({
          queryKey: accountListKey,
          exact: true,
          refetchType: 'none',
        });

      if (inFlightListGet) {
        // Fire-and-forget — do NOT await (that is what stalled the mutation).
        inFlightListGet.promise
          .then(() => {
            // The GET landed: it just committed a read-after-write STALE list to
            // the cache, so re-apply the optimistic mutation on top. Idempotent
            // (upsert overlays by key, remove is a no-op when nothing matches), so
            // a late detached reapply merges THIS mutation's row rather than
            // clobbering a concurrent mutation's write. Write the query we CAPTURED
            // by its exact key directly rather than via the active-only
            // `seedAccountList`: if the list component unmounted before the GET
            // resolved the query went inactive, but the stale GET still commits to
            // this exact cache entry regardless of observers (TanStack does not abort
            // on unmount), so the inactive-query write still lands; the active-only
            // seed would find no active match and silently strand the stale list.
            // `inFlightListGet.queryKey` is byte-for-byte the current principal's
            // suffixed `accountListKey`, so tenant isolation (ENG-4400) is unchanged.
            queryClient.setQueryData<AccountCache>(
              inFlightListGet.queryKey,
              applyOptimistic
            );
            // AFTER the reapply (see ORDERING GUARANTEE above): re-mark stale so a
            // remount refetches.
            markAccountListStale();
          })
          .catch(() => {
            // The GET errored/aborted: nothing landed to clobber, so the immediate
            // optimistic seed above still stands. Nothing to re-apply — but still
            // mark stale so a future natural trigger reconciles.
            markAccountListStale();
          });
      } else {
        // No in-flight GET: the immediate seed above is authoritative; just mark it
        // stale so the next natural trigger reconciles it.
        markAccountListStale();
      }

      // Residual root cause (the unbounded list GET has no axios timeout, so it can hang
      // arbitrarily long) is tracked in ENG-4992; this decouple stops that from stalling
      // the mutation regardless of how long the GET takes.

      // (2) Every OTHER account query variant — the filtered/parameterized
      // queries (e.g. `useUserSettings`' ['MY','account','#…#settings', friend],
      // or a ['MY','account','#me#<q>'] search). Nothing was seeded into these,
      // so the old blanket refetchType:'none' left them marked stale yet never
      // refetched → stale until focus/remount (Direction 2). Let them refetch
      // (default refetchType 'active') so they reconcile against the backend.
      queryClient.invalidateQueries({
        predicate: query => {
          const key = query.queryKey as unknown[];
          return (
            key[0] === 'MY' &&
            key[1] === 'account' &&
            !(
              key.length === accountListKey.length &&
              key.every((seg, i) => seg === accountListKey[i])
            )
          );
        },
      });
    },
  });
};

export const useUserSettings = (
  options?: UseMyOptions<'account'>,
  queryKey: string = 'settings'
) => {
  const { isImpersonating, me, friend } = useAuth();

  const myQuery = useMy(
    {
      resource: 'account',
      query: `#${isImpersonating ? friend : me}#${queryKey}`,
    },
    {
      // ENG-4601: force focus re-validation on this parameterized settings
      // query. It is NOT seeded by useModifyAccount's onSuccess (only the exact
      // unfiltered account-list key is), so a race-losing active refetch there
      // can cache a stale response and mark it FRESH under the 5-min global
      // staleTime. The default refetchOnWindowFocus:true only refetches STALE
      // queries, so that stale-as-fresh entry would never reconcile on focus;
      // 'always' refetches regardless of staleness, restoring the focus-recovery
      // channel. Set as a DEFAULT — caller `options` is spread last so a caller
      // can still override it.
      refetchOnWindowFocus: 'always',
      ...options,
    }
  );

  return myQuery;
};

export function useGetCollaboratorsListFromAccounts(accounts: Account[]) {
  return useMemo(() => {
    return accounts.filter(
      acc => !acc.key.endsWith('#settings#') && acc?.member === acc?.username
    );
  }, [accounts]);
}

export function useGetCollaboratorEmails(accounts: Account[]) {
  return useMemo(() => {
    return accounts
      .filter(
        acc => !acc.key.endsWith('#settings#') && acc?.member === acc?.username
      )
      .map(acc => acc.name);
  }, [accounts]);
}
